import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import {
  LuArchive,
  LuArchiveRestore,
  LuCheck,
  LuChevronDown,
  LuCircleDot,
  LuTrash2,
  LuX,
} from "react-icons/lu";

import { WorkModel } from "../../../factories/works/models/work.model";
import {
  useArchiveOrUnarchiveMutation,
  useDeleteWorkMutation,
  useUpdateMultipleStatusesMutation,
} from "../../../slices/WorkSlice";
import { MULTIPLE_STATUSES } from "../utils/statuses";

/**
 * Selecting work items and acting on them (Change status / Archive /
 * Reactivate / Delete). Shared by the Work page and the Home page's
 * "Needs attention today" card (Roadmap 1875), so both behave the same.
 */

export const bulkButton =
  "inline-flex h-10 items-center gap-[7px] whitespace-nowrap rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-[15px] text-[13px] font-bold text-[#3A4256] hover:border-[#C5CCDA] hover:bg-[#FAFAFF] disabled:opacity-50";
const compactButton =
  "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-2.5 text-[12.5px] font-bold text-[#3A4256] hover:border-[#C5CCDA] hover:bg-[#FAFAFF] disabled:opacity-50";

/** The row / header checkbox of a work list. */
export const SelectBox: FC<{
  checked: boolean;
  onChange: () => void;
  label?: string;
}> = ({ checked, onChange, label }) => (
  <button
    type={"button"}
    aria-pressed={checked}
    aria-label={label || (checked ? "Unselect" : "Select")}
    data-testid={"work-select"}
    onClick={(event) => {
      event.stopPropagation();
      onChange();
    }}
    onKeyDown={(event) => event.stopPropagation()}
    className={classNames(
      "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] align-middle",
      checked
        ? "border-[#6B4EFF] bg-[#6B4EFF] text-white"
        : "border-[#C9CDD6] bg-white",
    )}>
    {checked && <LuCheck size={11} strokeWidth={3.5} />}
  </button>
);

/** "Change status" button + menu of the main statuses. */
export const BulkStatusMenu: FC<{
  disabled?: boolean;
  compact?: boolean;
  onPick: (status: string) => void;
}> = ({ disabled, compact, onPick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    // Esc closes the menu too (QA batch 12).
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  return (
    <div ref={ref} className={"relative"}>
      <button
        type={"button"}
        disabled={disabled}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={compact ? compactButton : bulkButton}>
        <LuCircleDot size={compact ? 14 : 16} strokeWidth={1.9} />
        Change status
        <LuChevronDown
          size={12}
          strokeWidth={2.4}
          className={"text-[#9097A8]"}
        />
      </button>
      {isOpen && (
        <div
          role={"menu"}
          className={
            "absolute left-0 top-full z-[31] mt-1.5 min-w-[180px] rounded-[10px] border border-[#E5E7EB] bg-white p-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
          }>
          {MULTIPLE_STATUSES.map((status) => (
            <button
              type={"button"}
              role={"menuitem"}
              key={status.statusId}
              onClick={() => {
                setIsOpen(false);
                onPick(status.name);
              }}
              className={
                "flex w-full items-center gap-2.5 whitespace-nowrap rounded-[7px] px-2.5 py-2 text-left text-[13.5px] font-semibold text-[#3A4256] hover:bg-[#F5F6F8]"
              }>
              <span
                className={"h-[9px] w-[9px] shrink-0 rounded-full"}
                style={{ backgroundColor: status.color }}
              />
              {status.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * "N selected" + the actions + clear. Active work: Change status / Archive
 * (Archive asks first: the caller opens WorksArchiveConfirmModal).
 * Archived work: Reactivate / Delete.
 */
export const WorkBulkBar: FC<{
  count: number;
  busy?: boolean;
  compact?: boolean;
  archived?: boolean;
  onChangeStatus?: (status: string) => void;
  onArchive?: () => void;
  onReactivate?: () => void;
  onDelete?: () => void;
  onClear: () => void;
}> = ({
  count,
  busy,
  compact,
  archived,
  onChangeStatus,
  onArchive,
  onReactivate,
  onDelete,
  onClear,
}) => {
  const button = compact ? compactButton : bulkButton;
  const icon = compact ? 14 : 16;
  return (
    <div
      data-testid={"work-bulk-bar"}
      className={classNames(
        "flex items-center",
        compact ? "gap-1.5" : "gap-[9px]",
      )}>
      <span
        className={
          "mr-1 whitespace-nowrap text-[13px] font-bold text-[#6B4EFF]"
        }>
        {count} selected
      </span>
      {archived ? (
        <>
          <button
            type={"button"}
            className={button}
            disabled={busy}
            onClick={onReactivate}>
            <LuArchiveRestore size={icon} strokeWidth={1.9} />
            Reactivate
          </button>
          <button
            type={"button"}
            className={button}
            disabled={busy}
            onClick={onDelete}>
            <LuTrash2 size={icon} strokeWidth={1.9} />
            Delete
          </button>
        </>
      ) : (
        <>
          <BulkStatusMenu
            disabled={busy}
            compact={compact}
            onPick={(status) => onChangeStatus?.(status)}
          />
          <button
            type={"button"}
            className={button}
            disabled={busy}
            onClick={onArchive}>
            <LuArchive size={icon} strokeWidth={1.9} />
            Archive
          </button>
        </>
      )}
      <button
        type={"button"}
        title={"Clear selection"}
        aria-label={"Clear selection"}
        onClick={onClear}
        className={classNames(
          "inline-flex items-center justify-center rounded-lg text-[#6B7185] hover:bg-[#F1F2F5]",
          compact ? "h-8 w-8" : "h-[34px] w-[34px]",
        )}>
        <LuX size={16} strokeWidth={2.2} />
      </button>
    </div>
  );
};

/** The server calls behind the bulk bar; each clears the selection when done. */
export const useWorkBulkActions = (
  selected: WorkModel[],
  setSelected: (works: WorkModel[]) => void,
) => {
  const [updateStatuses, { isLoading: isUpdatingStatus }] =
    useUpdateMultipleStatusesMutation();
  const [archiveOrUnarchive, { isLoading: isArchiving }] =
    useArchiveOrUnarchiveMutation();
  const [deleteWork, { isLoading: isDeleting }] = useDeleteWorkMutation();

  const selectedIds = selected.map((work) => work.workId);

  const changeStatus = async (status: string) => {
    if (!selectedIds.length) return;
    await updateStatuses({ body: selectedIds, status }).unwrap();
    setSelected([]);
  };
  const archive = async (archiveWork: boolean) => {
    if (!selectedIds.length) return;
    await archiveOrUnarchive({ body: selectedIds, archiveWork }).unwrap();
    setSelected([]);
  };
  const remove = async () => {
    if (!selectedIds.length) return;
    await deleteWork({ body: selectedIds }).unwrap();
    setSelected([]);
  };

  return {
    changeStatus,
    archive,
    remove,
    isBusy: isUpdatingStatus || isArchiving || isDeleting,
  };
};
